import React, { useState, useEffect } from "react";
import EntityForm from "@/modules/entity/forms";
import Form from "./form";
import CommitForm from "./comments";
import get from "lodash/get";
import moment from "moment";
import useAlert from "@/hooks/useAlert";
import qs from "qs";
import { Spinner } from "../../../components";
import { api, helpers } from "../../../services";
import EntityContainer from "../../../modules/entity/containers";
import { EditIcon } from "lucide-react";
import { LeftOutlined } from "@ant-design/icons";

const normalizeUser = (u, usersList = []) => {
  if (u === null || u === undefined) return null;
  if (typeof u === "object") {
    if (u.id === undefined || u.id === null) return null;
    return { id: u.id, name: u.name || `ID ${u.id}` };
  }
  const found = usersList.find((x) => x && String(x.id) === String(u));
  if (found && found.name) {
    return { id: found.id, name: found.name };
  }
  return { id: u, name: `ID ${u}` };
};

const extractUserIds = (row) => {
  if (!row) return [];
  const raw = row.user_ids;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {}
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [raw];
};

const getMultiUsers = (row, extraUsers = []) => {
  if (!row) return [];

  const knownUsers = [
    ...(Array.isArray(row.users) ? row.users : []),
    ...(Array.isArray(extraUsers) ? extraUsers : []),
    ...(row.user ? [row.user] : []),
  ];

  const rawUserIds = extractUserIds(row);
  const rawUsers = Array.isArray(row.users) ? row.users : [];

  const candidates = [
    ...rawUsers.map((item) => normalizeUser(item, knownUsers)),
    ...rawUserIds.map((item) => normalizeUser(item, knownUsers)),
  ];

  return candidates.filter(Boolean).reduce((acc, u) => {
    const existing = acc.find((x) => String(x.id) === String(u.id));
    if (!existing) {
      acc.push(u);
    } else if (
      (!existing.name || existing.name === `ID ${existing.id}`) &&
      u.name &&
      u.name !== `ID ${u.id}`
    ) {
      existing.name = u.name;
    }
    return acc;
  }, []);
};

const Update = ({
  showUpdateModal,
  selected,
  leadType = "online",
  hideBackButton = false,
}) => {
  const { showAlert } = useAlert();

  const [activeTab, setActiveTab] = useState("1");
  const [isCanUpdate, setIsCanUpdate] = useState(false);
  const [selectedCommit, setSelectedCommit] = useState(null);
  const [status, setStatus] = useState(null);
  const [resolvedUsers, setResolvedUsers] = useState([]);

  useEffect(() => {
    const rawList = getMultiUsers(selected);
    const needFetch = rawList.filter((u) => !u.name || u.name === `ID ${u.id}`);
    if (needFetch.length > 0) {
      api.request
        .get("/user", {
          params: {
            filter: { id: needFetch.map((u) => u.id) },
            per_page: 50,
          },
        })
        .then((res) => {
          const list = get(res, "data.data") || get(res, "data") || [];
          if (Array.isArray(list) && list.length > 0) {
            setResolvedUsers(list);
          }
        })
        .catch(() => {});
    }
  }, [selected]);

  const tabs = [
    {
      title: "Основные данные",
      value: "1",
    },
    {
      title: "История",
      value: "2",
    },
    {
      title: "Комментарии",
      value: "3",
    },
  ];

  const leadTitle =
    get(selected, "customer_name") ||
    get(selected, "translate.name") ||
    get(selected, "name") ||
    "Лид";
  const statusName = get(selected, "status.name");
  const statusColor = get(selected, "status.color", "#3b82f6");

  const multiUsers = getMultiUsers(selected, resolvedUsers);
  const singleUser = get(selected, "user");
  const allHeaderUsers = [
    ...(singleUser ? [singleUser] : []),
    ...multiUsers,
  ].reduce((acc, u) => {
    if (u && !acc.some((x) => String(x.id) === String(u.id))) acc.push(u);
    return acc;
  }, []);

  const userNames = allHeaderUsers
    .map((u) => {
      const name = get(u, "name");
      if (!name) return null;
      const phone = helpers.formatPhone(
        get(u, "phone") || get(u, "mobile_phone"),
      );
      return phone ? `${name} (${phone})` : name;
    })
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <div className="lead-chat-sticky-header flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {showUpdateModal &&
            !hideBackButton &&
            !window.location.pathname.includes("/leads-statistics") && (
              <button
                type="button"
                className="d-btn d-btn-default d-btn-icon !w-8 !h-8 !min-w-[32px] !rounded-lg !border !border-slate-200 hover:!bg-slate-100 transition !flex !items-center !justify-center"
                onClick={() => showUpdateModal(false)}
                title="Назад"
              >
                <LeftOutlined style={{ fontSize: "13px" }} />
              </button>
            )}
          <div className="min-w-0">
            <div className="font-semibold text-slate-800 text-[15px] leading-tight flex items-center gap-2 flex-wrap">
              <span className="truncate max-w-[220px]" title={leadTitle}>
                {leadTitle}
              </span>
              {statusName && (
                <span
                  className="text-[11px] px-2 py-0.5 rounded-full font-medium"
                  style={{
                    backgroundColor: `${statusColor}18`,
                    color: statusColor,
                    border: `1px solid ${statusColor}35`,
                  }}
                >
                  {statusName}
                </span>
              )}
            </div>
            {userNames && (
              <div className="text-[12px] text-slate-500 mt-0.5">
                Менеджер: {userNames}
              </div>
            )}
          </div>
        </div>

        <div className="bb-tab-buttons !mb-0 shrink-0">
          {tabs.map((tab) => (
            <div
              key={tab.value}
              className={`bb-tab-buttons__item !py-1 !px-2.5 !text-xs !font-medium ${
                activeTab === tab.value ? "active" : ""
              }`}
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.title}
            </div>
          ))}
        </div>
      </div>
      <div className="lead-update-form-body">
        {activeTab === "1" && (
          <EntityForm.Main
            method="put"
            entity="leads"
            name="all"
            url={`/employee/leads/${get(selected, "id")}`}
            normalizeData={(data) => data}
            id={get(selected, "id")}
            updateData={true}
            selfErrorMessage={true}
            onSuccess={(data, resetForm) => {
              showAlert("Сохранено", {
                type: "success",
                delay: 1000,
              });
            }}
            onError={(errors) => {
              showAlert(get(errors, "message"), { type: "danger" });
            }}
            params={{
              include:
                "activities,region,district,source,status,contacts,user,users,organization",
              extra: {
                append: "humanized_activities,type_of_activity",
              },
            }}
            fields={[
              {
                name: "customer_name",
                value: selected.customer_name
                  ? get(selected, "customer_name")
                  : get(selected, "translate.name", ""),
                required: true,
              },
              {
                name: "phone",
                value: get(selected, "phone"),
              },
              {
                name: "region_id",
                value: get(selected, "region"),
                onSubmitValue: (value) => get(value, "id"),
              },
              {
                name: "district_id",
                value: get(selected, "district"),
                onSubmitValue: (value) => get(value, "id"),
              },
              {
                name: "type_of_activity_id",
                value: get(selected, "type_of_activity"),
                onSubmitValue: (value) => get(value, "id"),
              },
              {
                name: "source_id",
                value: get(selected, "source"),
                onSubmitValue: (value) => get(value, "id"),
              },
              {
                name: "status_id",
                value: get(selected, "status"),
                onSubmitValue: (value) => get(value, "id"),
              },
              {
                name: "user_id",
                value:
                  get(selected, "user") ||
                  (get(selected, "user_id")
                    ? normalizeUser(get(selected, "user_id"), resolvedUsers)
                    : null),
                onSubmitValue: (value) => get(value, "id"),
              },
              {
                name: "user_ids",
                value: multiUsers,
                onSubmitValue: (value) =>
                  Array.isArray(value) ? value.map((v) => get(v, "id", v)) : [],
              },
              {
                name: "cancellation_reason_id",
                value: get(selected, "cancellation_reason_id"),
                onSubmitValue: (value) => get(value, "id"),
                required: status === 12 ? true : false,
              },
              {
                name: "quantity",
                value: get(selected, "quantity"),
              },
              {
                name: "phone",
                value: get(selected, "phone"),
              },
              {
                name: "type",
                value: get(selected, "type"),
              },
              {
                name: "quality",
                value:
                  get(selected, "quality") === 0
                    ? String(get(selected, "quality"))
                    : get(selected, "quality"),
              },
              {
                name: "agreement",
                value:
                  selected &&
                  selected.agreement !== undefined &&
                  selected.agreement !== null
                    ? Number(selected.agreement)
                    : undefined,
                onSubmitValue: (value) =>
                  value !== undefined && value !== null && value !== ""
                    ? Number(value)
                    : undefined,
              },
              {
                name: "agreement_at",
                value: get(selected, "agreement_at"),
                onSubmitValue: (value) =>
                  value ? moment(value).format("YYYY-MM-DD HH:mm") : undefined,
              },
              {
                name: "contacts",
                value: get(selected, "contacts"),
              },
              {
                name: "created_at",
                value: get(selected, "created_at")
                  ? typeof get(selected, "created_at") === "number"
                    ? moment.unix(get(selected, "created_at")).toDate()
                    : moment(get(selected, "created_at")).toDate()
                  : undefined,
                onSubmitValue: (value) =>
                  value ? moment(value).format("YYYY-MM-DD HH:mm") : undefined,
              },
              {
                name: "installed_at",
                value: get(selected, "installed_at"),
              },
              {
                name: "meeting_at",
                value: get(selected, "meeting_at") ?? null,
                onSubmitValue: (value) => value ?? null,
              },
              {
                name: "meeting_status",
                value: get(selected, "meeting_status") ?? null,
                onSubmitValue: (value) => value ?? null,
              },
              {
                name: "sold_at",
                value: get(selected, "sold_at"),
              },
            ]}
          >
            {({ isSubmitting, values, setFieldValue }) => {
              return (
                <div>
                  <Form
                    {...{
                      setStatus,
                      status,
                      isSubmitting,
                      values,
                      setFieldValue,
                      isUpdate: true,
                      selected,
                      leadType,
                    }}
                  />
                </div>
              );
            }}
          </EntityForm.Main>
        )}
        {activeTab === "2" && (
          <div className="space-y-2.5 overflow-y-auto max-h-[75vh] pr-1">
            {selected &&
            selected.humanized_activities &&
            selected.humanized_activities.length > 0 ? (
              selected.humanized_activities.map((activity, idx) => (
                <div
                  key={idx}
                  className="p-3 bg-slate-50 border border-slate-200/80 rounded-xl hover:bg-slate-100/70 transition"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-medium text-xs text-slate-800 leading-snug">
                      {activity.messages.map((message, mIdx) => (
                        <div key={mIdx} className="mb-0.5">
                          {message}
                        </div>
                      ))}
                    </div>
                    {activity.meta?.time && (
                      <span className="text-[11px] text-slate-400 shrink-0 font-medium">
                        {activity.meta.time}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-1">
                    {activity.meta?.at && <span>{activity.meta.at}</span>}
                    {activity.meta?.by && <span>{activity.meta.by}</span>}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-slate-400 text-sm">
                История отсутствует
              </div>
            )}
          </div>
        )}
        {activeTab === "3" && (
          <div className="space-y-3">
            <EntityForm.Main
              method={selectedCommit ? "put" : `post`}
              entity="comments"
              name="all"
              url={
                selectedCommit
                  ? `/comments/${get(selectedCommit, "id")}`
                  : `comments`
              }
              normalizeData={(data) => data}
              updateData={selectedCommit}
              selfErrorMessage={true}
              onSuccess={(data, resetForm) => {
                showAlert("Сохранено", {
                  type: "success",
                  delay: 1000,
                });
                resetForm();
                setSelectedCommit(null);
                setIsCanUpdate(!isCanUpdate);
              }}
              onError={(errors) => {
                showAlert(get(errors, "message"), { type: "danger" });
              }}
              fields={[
                {
                  name: "modelable_id",
                  value: selected.id
                    ? get(selected, "id")
                    : get(selected, "translate.name", ""),
                  required: true,
                },
                {
                  name: "body",
                  value: get(selectedCommit, "body"),
                },
                {
                  name: "modelable_type",
                  value: "lead",
                },
              ]}
            >
              {({ isSubmitting, values, setFieldValue }) => {
                return (
                  <div>
                    <CommitForm
                      {...{
                        isSubmitting,
                        values,
                        setFieldValue,
                        isUpdate: selectedCommit,
                        setSelectedCommit,
                      }}
                    />
                  </div>
                );
              }}
            </EntityForm.Main>
            <EntityContainer.All
              entity="comments"
              name={`all`}
              canUpdate={isCanUpdate}
              url={`/comments`}
              primaryKey="id"
              params={{
                limit: 100,
                include: "user",
                extra: {
                  modelable_type: "lead",
                  modelable_id: get(selected, "id"),
                },
              }}
            >
              {({ items, isFetched, meta }) => {
                return (
                  <div className="space-y-2.5 mt-3 overflow-y-auto max-h-[55vh] pr-1 relative">
                    {!isFetched && <Spinner position={"absolute"} />}
                    {items && items.length > 0 ? (
                      items.map((item) => (
                        <div
                          key={item.id}
                          className="p-3 bg-slate-50 border border-slate-200/80 rounded-xl flex items-start justify-between gap-3 hover:bg-slate-100/70 transition"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-xs text-slate-800">
                                {get(item, "user.name", "Пользователь")}
                              </span>
                              <span className="text-[11px] text-slate-400">
                                {helpers.formatDate(
                                  get(item, "created_at"),
                                  "DD.MM.YYYY / HH:mm:ss",
                                )}
                              </span>
                            </div>
                            <p className="text-xs text-slate-700 m-0 leading-relaxed whitespace-pre-wrap">
                              {item.body}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSelectedCommit(item)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 border border-transparent hover:border-blue-100 transition shrink-0"
                            title="Редактировать"
                          >
                            <EditIcon size={14} />
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-10 text-slate-400 text-sm">
                        Комментариев пока нет
                      </div>
                    )}
                  </div>
                );
              }}
            </EntityContainer.All>
          </div>
        )}
      </div>
    </>
  );
};

export default Update;
